import { Injectable, Type, inject } from '@angular/core';
import { Router } from '@angular/router';
import { ModalController, NavController, ToastController } from '@ionic/angular/standalone';
import { OverlayEventDetail } from '@ionic/core';
import * as Sentry from '@sentry/angular';
import { Charity } from '../../models/Charity';
import { TabService } from './tab.service';
import { ModalRegistryService, RegisteredModal } from './modal-registry.service';
import { ChunkErrorHandler } from '../error/chunk-error-handler';
import { markUserNotified, portalFromPathname } from '../error/app-error-handler';

const MODAL_ERROR_TOAST_MESSAGE: string = "Couldn't open this screen. Please try again.";

@Injectable({ providedIn: 'root' })
export class ModalService {
  private modalRegistry = inject(ModalRegistryService);
  private router = inject(Router);
  private toastController = inject(ToastController);
  // Optional: `ChunkErrorHandler` is a plain `@Injectable()` (no `providedIn: 'root'`) —
  // `main.ts` provides it explicitly for the real app, but the many pre-existing specs that
  // construct `ModalService` only transitively (as some other component's dependency) never had
  // reason to provide it. A required injection would NullInjectorError at construction time in
  // every one of those, before `showModal` ever runs. Optional keeps production byte-identical
  // (always provided there) and degrades safely in an unconfigured test injector: an absent
  // handler is treated as "not a chunk error" below, the more conservative default (still
  // captures + toasts rather than silently swallowing).
  private chunkErrorHandler = inject(ChunkErrorHandler, { optional: true });

  constructor(
    private modalCtrl: ModalController,
    private navCtrl: NavController,
    private tabService: TabService
  ) {}

  /**
   * Captures current URL for modal return if URL is valid.
   * Always overwrites any prior value so a stale URL from an abandoned flow
   * (e.g. reload or navigation while a previous modal was open) cannot redirect
   * the user to a page they have since left. Chained flows (guest → signup → login)
   * are unaffected because modals are overlays — router.url is unchanged between
   * the chained captures.
   */
  private captureModalReturnUrlIfNeeded(): void {
    const currentUrl: string = this.router.url;
    if (this.tabService.isValidReturnUrl(currentUrl)) {
      sessionStorage.setItem('modalReturnUrl', currentUrl);
    }
  }

  async showModal(
    key: string,
    props?: Record<string, unknown>,
    overrides?: { cssClass?: string; backdropDismiss?: boolean; showBackdrop?: boolean }
  ): Promise<HTMLIonModalElement> {
    try {
      const registration: RegisteredModal | undefined = this.modalRegistry.get(key);
      if (!registration) {
        throw new Error(`[ModalService] No modal registered for key '${key}'`);
      }
      const component: Type<unknown> = await registration.loadComponent();
      const modal: HTMLIonModalElement = await this.modalCtrl.create({
        component,
        componentProps: { ...registration.defaultProps, ...props },
        cssClass: overrides?.cssClass ?? registration.cssClass,
        backdropDismiss: overrides?.backdropDismiss ?? registration.backdropDismiss,
        showBackdrop: overrides?.showBackdrop ?? registration.showBackdrop,
      });
      await modal.present();
      return modal;
    } catch (error) {
      if (this.chunkErrorHandler?.isChunkLoadError(error) ?? false) {
        // Rethrow only — the caller's unhandled rejection reaches AppErrorHandler, which routes
        // chunk errors to ChunkErrorHandler's own capture + reload UX. A local toast/capture here
        // would double-notify/double-capture (SUN-692 Decision 7).
        throw error;
      }

      this.captureToSentry(error, { modalKey: key });
      void this.presentModalErrorToast();
      // So AppErrorHandler's generic toast stays silent when this rethrow reaches it — our
      // tagged capture above already ran; the SDK's dedupeIntegration drops the later untagged
      // capture that would otherwise happen at the global handler (SUN-692 Decision 7).
      markUserNotified(error);
      throw error;
    }
  }

  private async presentModalErrorToast(): Promise<void> {
    try {
      const toast: HTMLIonToastElement = await this.toastController.create({
        message: MODAL_ERROR_TOAST_MESSAGE,
        color: 'warning',
        duration: 4000,
        position: 'top',
      });
      await toast.present();
    } catch {
      // Toast presentation must never crash the app.
    }
  }

  // Testability seam (same pattern as ErrorBoundaryDirective/AppErrorHandler): `@sentry/angular`'s
  // named exports compile to non-configurable getters under this project's webpack/Karma setup,
  // so `spyOn(Sentry, 'captureException')` cannot intercept the call — specs replace this method
  // directly instead of spying on the Sentry module.
  protected captureToSentry(error: unknown, tags: Record<string, string | boolean>): void {
    try {
      Sentry.captureException(error, { tags: { ...tags, portal: portalFromPathname() } });
    } catch {
      // Telemetry must never crash the app.
    }
  }

  async showGuestModal(alertMessage?: string, page?: string, minimal?: boolean): Promise<void> {
    this.captureModalReturnUrlIfNeeded();

    const modal: HTMLIonModalElement = await this.showModal(
      'guest',
      { alertMessage, minimal: minimal ?? false },
      { backdropDismiss: !!minimal }
    );

    const result: OverlayEventDetail = await modal.onDidDismiss();
    const role: string | undefined = result.role;
    if (role === 'cancel' && page === 'home') {
      sessionStorage.removeItem('modalReturnUrl');
      this.tabService.setLastTab('user', 'discover');
      this.navCtrl.navigateRoot('/main/discover');
    } else if (role === 'login') {
      this.showLoginModal(page);
    } else if (role === 'signup') {
      this.showSignupModal(page);
    } else {
      // Catch-all for cancel, backdrop dismiss, or any unhandled role.
      // Minimal guest modals allow backdrop dismiss (role !== 'cancel'),
      // so a strict 'cancel' check would leave modalReturnUrl stale.
      sessionStorage.removeItem('modalReturnUrl');
    }
  }

  async showSignupModal(page?: string): Promise<void> {
    this.captureModalReturnUrlIfNeeded();

    const modal: HTMLIonModalElement = await this.showModal('signup');

    const result: OverlayEventDetail = await modal.onDidDismiss();
    const role: string | undefined = result.role;
    if (role === 'cancel' && page === 'home') {
      sessionStorage.removeItem('modalReturnUrl');
      this.tabService.setLastTab('user', 'discover');
      this.navCtrl.navigateRoot('/main/discover');
    } else if (role === 'success') {
      setTimeout(() => {
        this.showLoginModal(page);
      }, 500);
    } else if (role === 'google-success') {
      const modalReturnUrl: string | null = sessionStorage.getItem('modalReturnUrl');
      if (modalReturnUrl) {
        sessionStorage.removeItem('modalReturnUrl');
        if (this.tabService.isValidReturnUrl(modalReturnUrl)) {
          // Clear any stale guard returnUrl so a later auth doesn't redirect to an
          // outdated protected route via getPostAuthRouteWithReturnUrl.
          sessionStorage.removeItem('returnUrl');
          this.tabService.setTabContextFromUrl(modalReturnUrl);
          if (this.router.url !== modalReturnUrl) {
            this.navCtrl.navigateRoot(modalReturnUrl);
          }
          return;
        }
      }
      this.navCtrl.navigateRoot(this.tabService.getPostAuthRouteWithReturnUrl());
    } else if (role === 'google-needs-completion') {
      // Modal component already navigated to /complete-google-profile with router state
      // (googleUserInfo, temporaryToken). Avoid a second navigateRoot here — it would
      // drop that state and bounce the user to /login.
      const modalReturnUrl: string | null = sessionStorage.getItem('modalReturnUrl');
      if (modalReturnUrl && this.tabService.isValidReturnUrl(modalReturnUrl)) {
        sessionStorage.setItem('returnUrl', modalReturnUrl);
        sessionStorage.removeItem('modalReturnUrl');
      }
    } else {
      // Catch-all for cancel, backdrop dismiss, or any unhandled role.
      sessionStorage.removeItem('modalReturnUrl');
    }
  }

  async showLoginModal(page?: string): Promise<void> {
    this.captureModalReturnUrlIfNeeded();

    const modal: HTMLIonModalElement = await this.showModal('login');

    const result: OverlayEventDetail = await modal.onDidDismiss();
    const role: string | undefined = result.role;

    if (role === 'success') {
      // PRIORITY 1: Validated modal flow
      const modalReturnUrl: string | null = sessionStorage.getItem('modalReturnUrl');
      if (modalReturnUrl) {
        sessionStorage.removeItem('modalReturnUrl');
        if (this.tabService.isValidReturnUrl(modalReturnUrl)) {
          // Clear any stale guard returnUrl so a later auth doesn't redirect to an
          // outdated protected route via getPostAuthRouteWithReturnUrl.
          sessionStorage.removeItem('returnUrl');
          this.tabService.setTabContextFromUrl(modalReturnUrl);
          if (this.router.url !== modalReturnUrl) {
            this.navCtrl.navigateRoot(modalReturnUrl);
          }
          return;
        }
      }

      // PRIORITY 2: Auth guard flow (protected route redirect)
      const guardReturnUrl: string | null = sessionStorage.getItem('returnUrl');
      if (guardReturnUrl && this.tabService.isValidReturnUrl(guardReturnUrl)) {
        this.navCtrl.navigateRoot(this.tabService.getPostAuthRouteWithReturnUrl());
        return;
      }

      // PRIORITY 3: Fallback (welcome page, etc.)
      this.navCtrl.navigateRoot(this.tabService.getPostAuthRoute());
      return;
    }

    if (role === 'google-needs-completion') {
      // Modal component already navigated to /complete-google-profile with router state
      // (googleUserInfo, temporaryToken). Avoid a second navigateRoot here — it would
      // drop that state and bounce the user to /login.
      const modalReturnUrl: string | null = sessionStorage.getItem('modalReturnUrl');
      if (modalReturnUrl && this.tabService.isValidReturnUrl(modalReturnUrl)) {
        sessionStorage.setItem('returnUrl', modalReturnUrl);
        sessionStorage.removeItem('modalReturnUrl');
      }
      return;
    }

    if (role === 'cancel' && page === 'home') {
      sessionStorage.removeItem('modalReturnUrl');
      this.tabService.setLastTab('user', 'discover');
      this.navCtrl.navigateRoot('/main/discover');
    } else {
      // Catch-all for cancel, backdrop dismiss, or any unhandled role.
      sessionStorage.removeItem('modalReturnUrl');
    }
  }

  async showEventModal(signUp?: boolean): Promise<HTMLIonModalElement> {
    const cssClass: string = signUp ? 'recover-guest-modal' : 'event-modal';
    const modal: HTMLIonModalElement = await this.showModal(
      'event',
      { signUp },
      { cssClass }
    );

    modal.onDidDismiss().then((result: OverlayEventDetail) => {
      const role: string | undefined = result.role;
      if (role === 'login') {
        this.showLoginModal();
      } else if (role === 'signup') {
        this.showSignupModal();
      }
    });
    return modal;
  }

  async showClaimCharityModal(userId: string, charity: Charity): Promise<HTMLIonModalElement> {
    return this.showModal('claim-charity', { userId, charity });
  }

  async showClaimBusinessModal(userId: string): Promise<HTMLIonModalElement> {
    return this.showModal('claim-business', { userId });
  }

  dismiss(): void {
    this.modalCtrl.dismiss();
  }
}
